import React from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import InvestmentDisclaimer from "@/components/InvestmentDisclaimer";

const headlineMetrics = [
  { v: "US$158M+", l: "Projected platform value" },
  { v: "US$25–35M", l: "Indicative acquisition range" },
  { v: "Layou, SVG", l: "Beachfront site" },
  { v: "3 Verticals", l: "Residential · Hospitality · Healthcare" },
];

const highlights = [
  { t: "Scarce Asset", b: "One of the last remaining large-scale beachfront development sites in Saint Vincent." },
  { t: "De-Risked Platform", b: "Historical approvals, planning groundwork and strong foundational work already completed." },
  { t: "Attractive Returns", b: "Clear pathway to substantial value creation across multiple revenue streams." },
  { t: "Strong Execution Team", b: "Experienced leadership across capital formation, hospitality, healthcare, development and government relations." },
  { t: "Sustainable Impact", b: "Long-term economic, social and environmental benefits for Saint Vincent & the Grenadines." },
  { t: "Scalable Platform", b: "Designed for future expansion and replication across the Caribbean." },
];

const verticals = [
  {
    title: "Residential",
    detail: "70–80 luxury villas and branded residences",
    body: "A curated luxury residential offering with branded residences and private villas — tenure structures designed for international owners and long-term stewardship.",
    image: "https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/0owokzqi_IMG_0620.jpeg",
  },
  {
    title: "Hospitality",
    detail: "100-key premium hotel and branded resort",
    body: "Internationally branded resort operations creating a destination-grade visitor experience, hospitality jobs and tourism receipts.",
    image: "https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/crupgogi_IMG_0622.jpeg",
  },
  {
    title: "Healthcare",
    detail: "30–40 bed private hospital and wellness",
    body: "Healthcare and wellness infrastructure addressing national healthcare capacity and service gaps — anchoring measurable community impact alongside commercial performance.",
    image: "https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/yef0jtnt_IMG_0621.jpeg",
  },
];

const structureRows = [
  { l: "Investor Capital", v: "Institutional / Family Office / HNW Investors" },
  { l: "Evergreen Global Holdings Ltd (UK)", v: "Holding Company" },
  { l: "Acquire / Control", v: "100% Ownership" },
  { l: "Bambereaux Development Ltd (BVI)", v: "Asset Holding Company (Bluesteel International Ltd)" },
  { l: "Project SPV", v: "Development Vehicle — Residential · Hospitality · Healthcare" },
];

const protections = [
  { t: "Investor Board Representation", b: "Active oversight and governance." },
  { t: "Reserved Matters", b: "Key decisions require investor approval." },
  { t: "Information Rights", b: "Regular reporting and transparency." },
  { t: "Step-In Rights", b: "Right to step in upon default." },
  { t: "Security Protections", b: "Share pledges and asset security." },
  { t: "Alignment of Interests", b: "Management and investor alignment." },
];

const phases = [
  {
    phase: "Phase 0",
    title: "Acquisition & Validation",
    range: "0–6 months",
    capital: "US$10.4M",
    capitalLabel: "Phase 0 capital requirement",
    items: [
      "Acquire & control Bluesteel International Ltd",
      "Verify ownership & title",
      "Validate planning approvals & development pathway",
      "Establish governance, leadership & teams",
      "Healthcare demand validation",
      "Finalise platform strategy",
    ],
  },
  {
    phase: "Phase 1",
    title: "Reactivated Platform Value",
    range: "Platform value",
    capital: "US$25–35M",
    capitalLabel: "Indicative platform value",
    items: [
      "De-risked, controlled and investor-ready platform",
      "All major uncertainties materially reduced",
      "Strong foundation for development execution",
      "Significant value uplift from acquisition",
    ],
  },
  {
    phase: "Phase 2",
    title: "Development Execution",
    range: "6–48 months",
    capital: "US$80–100M+",
    capitalLabel: "Estimated development capital",
    items: [
      "Construct residential, hotel & healthcare assets",
      "Deliver premium infrastructure",
      "Generate multiple revenue streams",
      "Build long-term cash flow",
      "Create employment and local value",
    ],
  },
  {
    phase: "Phase 3",
    title: "Long-Term Platform Value",
    range: "Stabilised",
    capital: "US$158M+",
    capitalLabel: "Projected long-term value",
    items: [
      "Integrated residential, hospitality & healthcare platform",
      "Sustainable cash flows",
      "Strong community and economic impact",
      "Scalable across the Caribbean",
    ],
  },
];

const valueDrivers = [
  { t: "Prime Location", b: "Scarce beachfront site in Layou with exceptional natural assets and accessibility." },
  { t: "Scarce Coastline Asset", b: "Limited large-scale beachfront development opportunities in Saint Vincent." },
  { t: "Multiple Revenue Streams", b: "Residential, hospitality, healthcare, retail and wellness-driven income diversity." },
  { t: "Healthcare Infrastructure Need", b: "Addressing national healthcare capacity and service gaps." },
  { t: "Institutional Governance", b: "Robust governance framework ensuring transparency, oversight and investor protection." },
  { t: "Scalable Replication", b: "Proven platform designed for replication across the Caribbean region." },
];

const leadership = [
  {
    name: "Jason Kehoe",
    role: "Founder & Executive Chairman",
    org: "Evergreen Global Holdings Ltd",
    quote: "Responsible for establishing and leading Evergreen Global Holdings Ltd and sponsoring the Bambereaux platform.",
    tags: ["Project Sponsor", "Capital Formation", "Strategic Direction", "Investor Engagement"],
  },
  {
    name: "Ian Jarvis",
    role: "Executive Chairman",
    org: "Bambereaux Integrated Development Platform",
    quote: "Responsible for development of the integrated investment platform and investor readiness programme.",
    tags: ["Platform Strategy", "Governance Framework", "Healthcare & Hospitality Integration", "Execution Oversight"],
  },
  {
    name: "Paul Clark",
    role: "Board Director — Hospitality & Real Estate",
    org: "Former Virgin Hotels Executive",
    quote: "Provides international hospitality and destination development expertise.",
    tags: ["Hospitality Strategy", "Hotel Operator Engagement", "Real Estate Development", "Commercial Validation"],
  },
  {
    name: "Dr Carlon Fitzpatrick",
    role: "Board Director — Clinical Governance & Healthcare Strategy",
    org: "",
    quote: "Leads healthcare strategy, governance and healthcare validation.",
    tags: ["Clinical Governance", "Healthcare Infrastructure", "Workforce Strategy", "Outcome Measurement"],
  },
  {
    name: "Charles Giles",
    role: "Government & Stakeholder Relations",
    org: "",
    quote: "Supports alignment between the development platform, government stakeholders and national economic development objectives.",
    tags: ["Government Engagement", "Regulatory Liaison", "Strategic Partnerships", "Community Alignment"],
  },
  {
    name: "David Kenchington",
    role: "Bluesteel Representative & Asset Stakeholder",
    org: "",
    quote: "Provides continuity between the historical Bambereaux development platform and future execution.",
    tags: ["Bluesteel Representation", "Historical Knowledge", "Asset Stewardship", "Local Project Relationships"],
  },
];

export default function Bambereaux() {
  return (
    <>
      <PageHeader
        testId="bambereaux-header"
        eyebrow="Strategic Platform · 01"
        title="The Bambereaux Integrated Development Platform"
        intro="A rare opportunity to acquire and reactivate a premium, government-recognised destination development platform in Saint Vincent & the Grenadines — combining luxury residential, hospitality and healthcare."
        image="https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/0owokzqi_IMG_0620.jpeg"
        meta="Saint Vincent & the Grenadines · Confidential investor teaser"
      />

      {/* Metrics — editorial inline, no boxes */}
      <section data-testid="bambereaux-metrics" className="bg-white border-b border-[#0A1F44]/12">
        <div className="evg-container py-16 md:py-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-10 gap-y-12">
            {headlineMetrics.map((m, i) => (
              <div key={m.l} data-reveal data-reveal-delay={String(i * 100)}>
                <div className="font-serif text-4xl md:text-5xl text-[#0A1F44] tracking-tight">
                  {m.v}
                </div>
                <div className="evg-overline mt-4">{m.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Opportunity Snapshot */}
      <section data-testid="bambereaux-snapshot" className="evg-section bg-white">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-12">
            <div className="lg:col-span-5" data-reveal>
              <div className="evg-overline mb-8">Opportunity Snapshot</div>
              <h2 className="evg-display-lg">
                Acquire, control and unlock value through disciplined execution.
              </h2>
            </div>
            <div className="lg:col-span-7" data-reveal data-reveal-delay="200">
              <p className="evg-body">
                The Bambereaux Integrated Development Platform offers a rare opportunity to acquire
                and control Bluesteel International Ltd (BVI) and its development platform in
                Layou, Saint Vincent &amp; the Grenadines, and unlock significant value through
                disciplined execution and governance-led delivery.
              </p>
              <p className="evg-body mt-7">
                Masterplanned for integrated residential, hospitality and healthcare components on
                a scarce, large-scale beachfront site, the platform benefits from historical
                approvals, strong local relationships and a government-recognised development
                pathway.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Highlights — editorial rows */}
      <section data-testid="bambereaux-highlights" className="evg-section bg-[#F5F6F8]">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-16">
            <div className="lg:col-span-4" data-reveal>
              <div className="evg-overline mb-8">Investment Highlights</div>
              <h2 className="evg-display-lg">Six reasons institutional capital partners with us.</h2>
            </div>
            <div className="lg:col-span-8">
              {highlights.map((h, i) => (
                <div
                  key={h.t}
                  data-testid={`bambereaux-highlight-${i + 1}`}
                  data-reveal
                  data-reveal-delay={String((i % 3) * 100)}
                  className="py-9 border-t border-[#0A1F44]/12 last:border-b last:border-[#0A1F44]/12 grid grid-cols-12 gap-x-8 gap-y-3 items-baseline"
                >
                  <span className="col-span-12 md:col-span-1 evg-index">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="col-span-12 md:col-span-4 font-serif text-2xl">{h.t}</h3>
                  <p className="col-span-12 md:col-span-7 text-[15px] text-[#0A1F44]/72 leading-relaxed font-light">
                    {h.b}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Vision — three full-width editorial spreads */}
      <section data-testid="bambereaux-verticals" className="evg-section bg-white">
        <div className="evg-container">
          <div className="max-w-3xl pb-20" data-reveal>
            <div className="evg-overline mb-8">The Vision</div>
            <h2 className="evg-display-lg">One platform, three integrated value streams.</h2>
            <p className="evg-lead mt-8 max-w-2xl">
              A world-class, sustainable integrated development platform combining residential,
              hospitality and healthcare assets — delivering attractive risk-adjusted returns and
              long-term value for investors, stakeholders and the nation.
            </p>
          </div>

          <div className="space-y-28 md:space-y-36">
            {verticals.map((v, i) => (
              <article
                key={v.title}
                data-testid={`bambereaux-vertical-${i + 1}`}
                data-reveal
                className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-10 items-center"
              >
                <div className={`lg:col-span-7 evg-img-zoom ${i % 2 ? "lg:order-2" : ""}`}>
                  <div className="relative aspect-[5/4] overflow-hidden bg-[#0A1F44]">
                    <img
                      src={v.image}
                      alt={v.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className={`lg:col-span-5 ${i % 2 ? "lg:order-1" : ""}`}>
                  <div className="evg-index mb-6">0{i + 1}</div>
                  <h3 className="evg-display-md">{v.title}</h3>
                  <p className="evg-overline-gold mt-5">{v.detail}</p>
                  <p className="evg-body mt-8">{v.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Transaction Structure — editorial flow */}
      <section data-testid="bambereaux-structure" className="evg-section bg-[#0A1F44] text-white">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-12 pb-20">
            <div className="lg:col-span-5" data-reveal>
              <div className="evg-overline-gold mb-8">Transaction Structure</div>
              <h2 className="evg-display-lg text-white">
                A clear, protected pathway to value creation.
              </h2>
            </div>
            <div className="lg:col-span-7" data-reveal data-reveal-delay="200">
              <p className="text-[17px] leading-[1.8] text-white/72 font-light">
                A non-binding Letter of Intent has been agreed with the shareholders of Bluesteel
                International Ltd to support the next phase of due diligence, exclusivity and
                acquisition discussions, subject to definitive documentation.
              </p>
            </div>
          </div>

          <div className="mt-4">
            {structureRows.map((row, i) => (
              <div
                key={row.l}
                data-reveal
                data-reveal-delay={String(i * 80)}
                className="py-7 border-t border-white/15 last:border-b last:border-white/15 grid grid-cols-12 gap-x-8 gap-y-2 items-baseline"
              >
                <span className="col-span-12 md:col-span-1 evg-index evg-index-light">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="col-span-12 md:col-span-5 font-serif text-xl md:text-2xl text-white">
                  {row.l}
                </div>
                <div className="col-span-12 md:col-span-6 text-[14px] text-white/72 font-light leading-relaxed">
                  {row.v}
                </div>
              </div>
            ))}
          </div>

          {/* Investor Protections */}
          <div className="mt-28">
            <div className="evg-overline-gold mb-10" data-reveal>Governance & Investor Protections</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
              {protections.map((p, i) => (
                <div
                  key={p.t}
                  data-reveal
                  data-reveal-delay={String((i % 3) * 100)}
                  className="py-6 border-t border-white/15"
                >
                  <h3 className="font-serif text-xl text-white">{p.t}</h3>
                  <p className="mt-2 text-[14px] text-white/65 leading-relaxed font-light">{p.b}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap — editorial phases (kept structural cards, lighter framing) */}
      <section data-testid="bambereaux-roadmap" className="evg-section bg-white">
        <div className="evg-container">
          <div className="max-w-3xl pb-20" data-reveal>
            <div className="evg-overline mb-8">Feasibility & Value Creation Roadmap</div>
            <h2 className="evg-display-lg">From acquisition to a US$158M+ integrated platform.</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-12 gap-y-14">
            {phases.map((p, i) => (
              <div
                key={p.phase}
                data-testid={`bambereaux-phase-${i + 1}`}
                data-reveal
                data-reveal-delay={String(i * 100)}
                className="border-t border-[#D4AF37] pt-8"
              >
                <div className="evg-overline-gold">{p.phase}</div>
                <h3 className="font-serif text-2xl md:text-[28px] mt-4 leading-tight">{p.title}</h3>
                <p className="evg-meta mt-3">{p.range}</p>
                <div className="mt-8">
                  <div className="font-serif text-3xl md:text-4xl text-[#0A1F44] tracking-tight">
                    {p.capital}
                  </div>
                  <div className="evg-overline mt-3">{p.capitalLabel}</div>
                </div>
                <ul className="mt-8 space-y-2.5">
                  {p.items.map((it) => (
                    <li key={it} className="text-[13.5px] text-[#0A1F44]/72 leading-relaxed font-light">
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Drivers — editorial rows */}
      <section data-testid="bambereaux-value-drivers" className="evg-section bg-[#F5F6F8]">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-16">
            <div className="lg:col-span-4" data-reveal>
              <div className="evg-overline mb-8">Key Value Drivers</div>
              <h2 className="evg-display-lg">What underwrites the platform.</h2>
            </div>
            <div className="lg:col-span-8">
              {valueDrivers.map((d, i) => (
                <div
                  key={d.t}
                  data-reveal
                  data-reveal-delay={String((i % 3) * 100)}
                  className="py-9 border-t border-[#0A1F44]/12 last:border-b last:border-[#0A1F44]/12 grid grid-cols-12 gap-x-8 gap-y-3 items-baseline"
                >
                  <span className="col-span-12 md:col-span-1 evg-index">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="col-span-12 md:col-span-4 font-serif text-2xl">{d.t}</h3>
                  <p className="col-span-12 md:col-span-7 text-[15px] text-[#0A1F44]/72 leading-relaxed font-light">
                    {d.b}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership — editorial roster */}
      <section data-testid="bambereaux-leadership" className="evg-section bg-white">
        <div className="evg-container">
          <div className="max-w-3xl pb-20" data-reveal>
            <div className="evg-overline mb-8">Leadership, Governance & Execution</div>
            <h2 className="evg-display-lg">The team driving the Bambereaux Integrated Development Platform.</h2>
            <p className="evg-lead mt-8 max-w-2xl">
              Experienced leadership across capital, development, hospitality, healthcare,
              government and local asset stewardship.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
            {leadership.map((p, i) => (
              <div
                key={p.name}
                data-testid={`bambereaux-leader-${p.name.split(" ")[0].toLowerCase()}`}
                data-reveal
                data-reveal-delay={String((i % 2) * 120)}
                className="pt-10 border-t border-[#0A1F44]/12"
              >
                <h3 className="font-serif text-3xl text-[#0A1F44] leading-tight">{p.name}</h3>
                <p className="mt-3 text-[14px] text-[#0A1F44]/75">{p.role}</p>
                {p.org ? <p className="text-[12px] text-[#0A1F44]/55 mt-1">{p.org}</p> : null}
                <p className="mt-6 text-[14px] italic text-[#0A1F44]/70 leading-[1.8] font-light">
                  “{p.quote}”
                </p>
                <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2">
                  {p.tags.map((t) => (
                    <span key={t} className="evg-meta">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-16 text-[12px] text-[#0A1F44]/50 font-light">
            All roles and responsibilities are as at the date of this presentation and may be
            subject to change.
          </p>
        </div>
      </section>

      {/* CTA + Disclaimer */}
      <section data-testid="bambereaux-cta" className="evg-section bg-[#F5F6F8]">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-12 items-end">
            <div className="lg:col-span-8" data-reveal>
              <div className="evg-overline mb-8">Investor Opportunity</div>
              <h2 className="evg-display-lg">Request the confidential investor teaser.</h2>
              <p className="evg-lead mt-8 max-w-xl">
                Building legacies. Creating value. Inspiring futures.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-6 lg:items-end" data-reveal data-reveal-delay="200">
              <a
                href="/documents/Bambereaux_Investor_Teaser.pdf"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="bambereaux-download-teaser"
                className="text-[11px] uppercase tracking-[0.24em] font-medium text-[#0A1F44] border-b border-[#0A1F44] pb-2 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors inline-flex items-center gap-2 self-start lg:self-end"
              >
                <Download size={14} /> Download Teaser
              </a>
              <Link
                to="/investor-relations"
                data-testid="bambereaux-cta-link"
                className="text-[11px] uppercase tracking-[0.24em] font-medium text-[#0A1F44] border-b border-[#D4AF37] pb-2 hover:text-[#D4AF37] transition-colors inline-block self-start lg:self-end"
              >
                Discuss Investment →
              </Link>
            </div>
          </div>
          <div className="mt-24 pt-12 border-t border-[#0A1F44]/12">
            <InvestmentDisclaimer />
          </div>
        </div>
      </section>
    </>
  );
}
